import React, { useState } from 'react';

import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import Text from '@components/common/Text/Text';
import potService from '@services/domain/Lpc/PotService';

import css from './CancelOffer.module.scss';

type CancelOfferProps = {
    potRef: string;
    customClass?: string;
    ctaLabel: string;
    potTitle?: string;
    benifFirstName?: string;
    benifLastName?: string;
    onUpdate: () => void;
    dashboardPage?: boolean;
};
const CancelOffer = ({
    potRef,
    customClass,
    ctaLabel,
    potTitle,
    benifFirstName,
    benifLastName,
    dashboardPage,
}: CancelOfferProps) => {
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    const [cancelOfferLoading, setCancelOfferLoading] = useState(false);
    const { t } = useTranslateLpc(['lpc-pot-history', 'common']);
    const { addToast } = useToasts();
    const router = useRouter();
    const cancelOfferPot = () => {
        setCancelOfferLoading(true);
        potService
            .cancelOfferPot(potRef)
            .then(() => {
                addToast(t('waiting-for-accept.cancel-cta.success'), {
                    appearance: 'success',
                    autoDismiss: true,
                });
                router.replace(router.asPath);
            })
            .catch(() => {
                addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                    appearance: 'error',
                    autoDismiss: true,
                });
            })
            .finally(() => {
                setShowCancelConfirmModal(false);
                setCancelOfferLoading(false);
            });
    };

    return (
        <>
            <div className={`${customClass || css.button_bloc} ${dashboardPage && css.button_bloc__dashboard}`}>
                <div
                    className={`${css.button_bloc__container} ${dashboardPage && css.button_bloc__container__dashboard}`}
                >
                    <img src="/front-static/icons/display/info-outline.svg" alt="info" />
                    <Text
                        variant={'body_02'}
                        color={'txt-primary'}
                        customClass={dashboardPage ? css.button_bloc__hint_dashboard : css.button_bloc__hint}
                        html
                        htmlText={t('waiting-for-accept.cancel-cta.hint-1')}
                    />
                </div>
                <Text
                    variant={'body_02'}
                    color={'txt-primary'}
                    customClass={dashboardPage ? css.button_bloc__hint_dashboard : css.button_bloc__hint}
                    html
                    htmlText={t('waiting-for-accept.cancel-cta.hint-2')}
                />
                <Button
                    customClass={`${customClass || css.button_bloc_cta}`}
                    variant={'secondary'}
                    onClick={(event) => {
                        event.preventDefault();
                        setShowCancelConfirmModal(true);
                    }}
                >
                    {ctaLabel}
                </Button>
            </div>
            <ConfirmModal
                title={t('waiting-for-accept.cancel-cta.title')}
                text={t('waiting-for-accept.cancel-cta.text', {
                    potTitle: potTitle,
                    beneficiaryFirstName: benifFirstName,
                    beneficiaryLastName: benifLastName,
                })}
                iconComponent={<IconCheckmark variant={'warning'} />}
                confirmLabel={t('waiting-for-accept.cancel-cta.cta')}
                cancelLabel={t('waiting-for-accept.cancel-cta.cancel')}
                isVisible={showCancelConfirmModal}
                onHide={() => setShowCancelConfirmModal(false)}
                isLoading={cancelOfferLoading}
                onConfirm={() => cancelOfferPot()}
            />
        </>
    );
};

export default CancelOffer;
